import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { EvaluationCompleteToast } from "@/components/context-console/EvaluationCompleteToast";
import { MESSAGES, ROUTES } from "@/constants";
import {
  dismissToast,
  showErrorToast,
  showToast,
  TOAST_CUSTOM_CLASS_NAMES,
  TOAST_DURATIONS,
  TOAST_STYLES,
} from "@/helpers/toast.helpers";
import { useEvaluationRun } from "@/hooks/useContextConsole";
import {
  EVALUATION_JOB_SET_ID_STORAGE_KEY as SET_ID_STORAGE_KEY,
  EVALUATION_JOB_STARTED_AT_STORAGE_KEY as STARTED_AT_STORAGE_KEY,
  EVALUATION_JOB_STORAGE_KEY as STORAGE_KEY,
  EVALUATION_JOB_VIEWED_STORAGE_KEY as VIEWED_STORAGE_KEY,
} from "./evaluationJobStorage";

const CONTEXT_CONSOLE_PATH = ROUTES.SETTINGS_CONTEXT_CONSOLE;
const NOTIFY = MESSAGES.CONTEXT_CONSOLE.NOTIFY;
const RUN_NOTIFY = NOTIFY.RUN;

interface EvaluationJobContextValue {
  jobId: string | null;
  /** The published set `jobId` was run against — known from the moment the run
   * starts, not from the (possibly still-pending) poll result. The page compares
   * this against the *currently* published set so a run from an earlier publish,
   * or an earlier browser session, never gets mistaken for a fresh one. */
  contextSetId: string | null;
  /** True once the admin has actually seen this job's result — either by
   * clicking the completion notification, or by having it render inline on
   * the Context Console page. Closing the notification does NOT set this;
   * that's what keeps the nav badge up as a reminder after a dismiss. */
  hasBeenViewed: boolean;
  /** `Date.now()` when the currently tracked job was started, or null when
   * nothing's tracked — drives the running-elapsed-time display. Survives a
   * refresh (sessionStorage-backed) like the rest of this state. */
  startedAt: number | null;
  /** Start tracking a freshly-created job — call right after the run kicks off. */
  startJob: (jobId: string, contextSetId: string) => void;
  /** Mark the current job's result as seen — clears the nav badge. */
  markViewed: () => void;
  /** Stop tracking (e.g. "Start over" on the Context Console page). */
  clearJob: () => void;
}

const EvaluationJobContext = createContext<EvaluationJobContextValue | undefined>(undefined);

/**
 * Tracks a Context Console accuracy run across navigation.
 *
 * `useEvaluationRun`'s poll only fires while a `useQuery` observer for it is
 * mounted — previously that observer lived on the Context Console page itself,
 * so leaving the page killed the poll and the run's outcome was silently lost.
 * Mounting the same poll here, at the app shell, keeps it alive for as long as
 * the tab is open; the job id survives a refresh via sessionStorage, and a
 * toast fires when the run settles while the user isn't looking at the
 * results already rendered on the Context Console page itself.
 */
export const EvaluationJobProvider = ({
  children,
  // Defaults to the "auth unresolved" sentinel, not a real identity — every
  // caller that doesn't care about the multi-user-on-one-tab scenario (most
  // tests) gets the pre-existing behavior unchanged; ContextConsoleProviders.tsx
  // is the one real caller that passes the actual signed-in user.
  identityKey = "anon",
}: {
  children: ReactNode;
  /** The signed-in user this instance is tracking a run for — `"anon"` while
   * auth is still resolving, or a real `userId`. See the identity-change
   * effect below for why this can't just be a React `key` instead. */
  identityKey?: string;
}) => {
  const [jobId, setJobId] = useState<string | null>(() => sessionStorage.getItem(STORAGE_KEY));
  const [contextSetId, setContextSetId] = useState<string | null>(() =>
    sessionStorage.getItem(SET_ID_STORAGE_KEY),
  );
  const [hasBeenViewed, setHasBeenViewed] = useState<boolean>(
    () => sessionStorage.getItem(VIEWED_STORAGE_KEY) === "true",
  );
  const [startedAt, setStartedAt] = useState<number | null>(() => {
    const stored = sessionStorage.getItem(STARTED_AT_STORAGE_KEY);
    return stored ? Number(stored) : null;
  });
  // Known, deliberately deferred: while identity is unresolved ("anon"), a
  // jobId resumed from sessionStorage still polls immediately rather than
  // waiting for a real identity — a leftover id from an already-ended
  // session fires one guaranteed-401 status request before anyone is known
  // to be signed in. Gating this on identityKey would also gate every
  // caller that doesn't pass one at all (every existing test), since
  // "anon" is also this prop's un-opted-in default — not worth the
  // complexity for what's a single harmless extra request.
  const { run, isDeadEnd } = useEvaluationRun(jobId);
  const notifiedJobIdRef = useRef<string | null>(null);
  const identityRef = useRef(identityKey);
  // True only once `startJob` has run in this tab's lifetime — a jobId seeded
  // from `sessionStorage` on mount (a restored tab, or a run left over from
  // earlier in the session before this provider existed) is real work worth
  // resuming the poll for, but its dead-end toast is not: it can land on any
  // page for a user who may not even hold the Context Console permission and
  // has no idea what "the accuracy run" refers to. A resumed id that turns
  // out to be dead just clears silently instead.
  const startedThisSessionRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const startJob = useCallback((id: string, setId: string) => {
    const now = Date.now();
    sessionStorage.setItem(STORAGE_KEY, id);
    sessionStorage.setItem(SET_ID_STORAGE_KEY, setId);
    sessionStorage.setItem(STARTED_AT_STORAGE_KEY, String(now));
    sessionStorage.removeItem(VIEWED_STORAGE_KEY);
    notifiedJobIdRef.current = null;
    startedThisSessionRef.current = true;
    setJobId(id);
    setContextSetId(setId);
    setHasBeenViewed(false);
    setStartedAt(now);
  }, []);

  const markViewed = useCallback(() => {
    sessionStorage.setItem(VIEWED_STORAGE_KEY, "true");
    setHasBeenViewed(true);
  }, []);

  const clearJob = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SET_ID_STORAGE_KEY);
    sessionStorage.removeItem(VIEWED_STORAGE_KEY);
    sessionStorage.removeItem(STARTED_AT_STORAGE_KEY);
    setJobId(null);
    setContextSetId(null);
    setHasBeenViewed(false);
    setStartedAt(null);
  }, []);

  useEffect(() => {
    // A same-tab account switch (Firebase fires null then a new uid, no
    // reload) must not leave one user's run tracked for the next. This can't
    // be "just clear sessionStorage from AuthContext's sign-out effect" —
    // that only reaches the persisted half of this state, not `run`/
    // `hasBeenViewed`, and can't be "remount via a React `key`" either: the
    // remount happens during render, before AuthContext's effect runs, so
    // the fresh instance's own useState initializer would read the very
    // sessionStorage entry that effect hasn't cleared yet. An explicit
    // effect reacting to the resolved identity sidesteps both problems.
    //
    // "anon" is ContextConsoleProviders' sentinel for "auth hasn't resolved
    // yet" — every mount transiently starts there before the real identity
    // (or logged-out) is known, including a same-user page refresh where a
    // legitimately resumed job should NOT be wiped. Only a transition AWAY
    // FROM a real, known identity — to a different user or to logged-out —
    // is a genuine switch worth clearing for.
    const previous = identityRef.current;
    identityRef.current = identityKey;
    if (previous !== "anon" && previous !== identityKey) clearJob();
    // clearJob is intentionally not a dep: it's stable (useCallback, no deps
    // of its own) and this effect must fire only on an identity change, not
    // on every clearJob identity churn (there isn't any, but re-running this
    // on anything but identityKey would defeat the whole point of the ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);

  useEffect(() => {
    // The poll has given up — a 404 (stale id surviving a sessionStorage-backed
    // refresh, or a run that predates a worker/DB reset) is one cause, but a
    // persistent 5xx or network error is just as much of a dead end: nothing
    // left is going to retry it. Either way, clear it so a stuck id doesn't
    // sit in sessionStorage forever with no future poll that could recover it.
    if (!jobId || !isDeadEnd) return;
    if (notifiedJobIdRef.current === jobId) return;
    notifiedJobIdRef.current = jobId;
    clearJob();
    // A job id merely resumed from sessionStorage (never started via startJob
    // in this provider's lifetime) turning out dead isn't news to the current
    // page's user — it may be a different user or a page with no idea what
    // "the accuracy run" is. Clear it quietly; only a run this tab actually
    // started gets the toast.
    if (startedThisSessionRef.current && !locationRef.current.startsWith(CONTEXT_CONSOLE_PATH)) {
      showErrorToast(
        "Lost track of the accuracy run",
        "Something interrupted tracking this run. Run it again from Context Console.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isDeadEnd]);

  useEffect(() => {
    if (!jobId || !run?.isSettled) return;
    if (notifiedJobIdRef.current === jobId) return;
    notifiedJobIdRef.current = jobId;

    // The Context Console page renders the settled result inline — a toast on
    // top of that would just be noise. Only notify when the user is elsewhere.
    if (locationRef.current.startsWith(CONTEXT_CONSOLE_PATH)) return;

    const toastId = `eval-job-${jobId}`;
    const handleView = () => {
      navigate(CONTEXT_CONSOLE_PATH);
      markViewed();
      dismissToast(toastId);
    };
    // Closing the notification only hides it — hasBeenViewed stays false, so
    // the Settings nav badge keeps reminding them until they actually look.
    const handleDismiss = () => dismissToast(toastId);

    const failed = run.status === "failed";
    const total = run.primary.total;
    const correct = run.primary.correct.length;

    showToast(
      <EvaluationCompleteToast
        variant={failed ? "error" : "success"}
        title={failed ? RUN_NOTIFY.FAILED_TITLE : RUN_NOTIFY.TITLE}
        description={
          failed
            ? run.error || RUN_NOTIFY.FAILED_BODY
            : total > 0
              ? RUN_NOTIFY.BODY(correct, total)
              : undefined
        }
        viewLabel={NOTIFY.VIEW}
        onView={handleView}
        onDismiss={handleDismiss}
      />,
      {
        id: toastId,
        duration: TOAST_DURATIONS.LOADING, // Infinity — stays until closed or viewed.
        style: failed ? TOAST_STYLES.error : TOAST_STYLES.success,
        classNames: TOAST_CUSTOM_CLASS_NAMES,
      },
    );
    // markViewed is stable (useCallback, no deps) — safe to list for real
    // now that it's memoized, rather than needing a disable comment.
  }, [jobId, run, navigate, markViewed]);

  // useEvaluationRun's poll updates `run` every POLL_INTERVAL_MS for the
  // duration of a multi-minute governed run — memoized so every consumer
  // (the Settings nav badge, the whole Context Console page) only re-renders
  // when something in the value actually changed, not on every tick.
  const value = useMemo(
    () => ({ jobId, contextSetId, hasBeenViewed, startedAt, startJob, markViewed, clearJob }),
    [jobId, contextSetId, hasBeenViewed, startedAt, startJob, markViewed, clearJob],
  );

  return <EvaluationJobContext.Provider value={value}>{children}</EvaluationJobContext.Provider>;
};

export const useEvaluationJob = (): EvaluationJobContextValue => {
  const context = useContext(EvaluationJobContext);
  if (context === undefined) {
    throw new Error("useEvaluationJob must be used within an EvaluationJobProvider");
  }
  return context;
};
